import React, { useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { restrictToVerticalAxis, restrictToWindowEdges } from "src/components/Sortable/Container/modifiers";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { IMdtba001, IMdtba002, IRefModalAddChangeDeleteAnswer, IRefModalAddChangeDeleteCriteria, IRefModalAddChangeDeleteQuestion} from "../../interface";
import ListOfCriteria from "./components/listOfCriteria";
import ModalAddChangeDeleteCriteria from "./components/modalAddChangeDeleteCriteria";
import ModalAddChangeDeleteAnswer from "./components/modalAddChangeDeleteAnswer";
import ModalAddChangeDeleteQuestion from "./components/modalAddChangeDeleteQuestion";

interface IBusinessDiagnosticsModel{
	modelSelected: IMdtba001
	modelSelectedTitle?: string;
}


const BusinessDiagnosticsModel: React.FC<IBusinessDiagnosticsModel> = ({
	modelSelected,
	modelSelectedTitle
}) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const refModalAddChangeDeleteCriteria = useRef<IRefModalAddChangeDeleteCriteria>(null);
	const refModalAddChangeDeleteQuestion = useRef<IRefModalAddChangeDeleteQuestion>(null);
	const refModalAddChangeDeleteAnswer = useRef<IRefModalAddChangeDeleteAnswer>(null);

	const [ data, setData] = useState<IMdtba002[] | null>(null);
	const [ tempData, setTempData] = useState<IMdtba002[] | null>(null);
	const [waitingForResponse, setWaitingForResponse] = useState(false);
	const [permissionsModel, setPermissionsModel] = useState(0);

	const { errorFetching, loading, loadData } = useEffectFetchData<{
		mdtba002: IMdtba002[],
		permissions: number
	}> (
		"DiagnosticModels/SelectModel", 
		{ 
			id: modelSelected?.mda001Id
		 },
	 ({ data: dataX }) => {
			setData(dataX.mdtba002);
			setPermissionsModel(dataX.permissions);
		},
		 [modelSelected]
	);

	const onCallbackAddOrChange = () => {
		loadData();
	};

	const onCallbackDelete = (id: number) => {
		setData((prevState) => prevState && prevState.filter((card) => card.mda002Id !== id));
	};

	const queryReorderCriteria = async (criteria: any[]) => {
		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "DiagnosticModels/UpdateOrderCriteria",
				body: {
					rows: criteria
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: translate("Critério movido com sucesso"),
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setData(tempData);
			standardErrorMessages(err, translate("Erro ao mover critério"), addToast);
			setWaitingForResponse(false);
			return false;
		}
	};

	return (
		<>
		
			<RCCGrid
				container 
				item
				justifyContent="space-between"
				xs={12}
			>
				<Title color="purple" size="xl">
					{modelSelectedTitle}
				</Title>
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
					{!errorFetching && !loading && data && permissionsModel >= 2 && (
						<RCCTooltip
							text={translate("Novo Critério")}
							position="leftEnd"
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => {refModalAddChangeDeleteCriteria.current?.actionButtonAdd(modelSelected.mda001Id);}}
								size="md"
							/>
						</RCCTooltip>
					)}													
				</CheckPermission>
				
			</RCCGrid>

			{errorFetching ? (
				<RCCAlert 
					color="red"
					title={translate("Atenção")}
					description={translate("Erro ao carregar dados")}
				/>
			): 
				<>
					{loading ? 
						<Loader /> : 
						<>
							{permissionsModel === 1 && (
								<RCCGrid
									container
									item
									alignItems="center"
								>
									<RCCIcon color="var(--grey)" size={14} name="infoCircle" />
									<Title color="grey" size="xs">
										{translate("Este modelo de diagnóstico já foi utilizado, algumas ações estarão desabilitadas")}.
									</Title>
								</RCCGrid>
							)}
							<RCCGrid styleInline="margin-top: 10px">
								{!data || data.length === 0 ? 
									(
										<RCCGrid 
											container 
											styleInline="margin: 10px"
										>
											<RCCAlert 
												title={translate("Atenção")}
												description={translate("Nenhum critério encontrado")}
											/>
										</RCCGrid>
									) : (
										<>
											<SortableContainer
												onDragStart={() => {
													setTempData(data);
												}}
  									onDragEnd={(activeIdX, overId) => {
													if(activeIdX === overId)
														return; 	

													const activeIndex = data.findIndex((checks) => checks.id === activeIdX);
													const overIndex = data.findIndex((checks) => checks.id === overId);

													const orderedItems = arrayMove(data, activeIndex, overIndex);

													setData(orderedItems);

													const rowsOrdered: any[] = [];

													orderedItems.forEach((item: any, index) => {
														const row = {
															mda002Id: item.mda002Id,
															mda002Ordem: index
														};
														rowsOrdered.push(row);
													});

													queryReorderCriteria(rowsOrdered);
												}}
												items={data.map((line) => ({
													...line,
													id: `${line.mda002Id.toString()}`
												}))}
												modifiers={[restrictToWindowEdges, restrictToVerticalAxis]}
  								>
												{data?.map((criteria) => (
													<div key={`container-criteria-${criteria.mda002Id}`}>
														<SortableItem 
															id={criteria.mda002Id.toString()} 
															key={criteria.mda002Id} 
															disabled={waitingForResponse}
														>
															<ListOfCriteria 
																criteriaData={criteria}
																refModalAddChangeDeleteCriteria={refModalAddChangeDeleteCriteria}
																refModalAddChangeDeleteAnswer={refModalAddChangeDeleteAnswer}
																refModalAddChangeDeleteQuestion={refModalAddChangeDeleteQuestion}
																permissions={permissionsModel}
															/>
														</SortableItem>
													</div>
												))}
											</SortableContainer>
										</>
									)
								}
							</RCCGrid>
						</>
					}
				</>
			}

			<ModalAddChangeDeleteAnswer 
				ref={refModalAddChangeDeleteAnswer} 
			/>
			
			<ModalAddChangeDeleteCriteria 
				onCallbackAddOrChange={onCallbackAddOrChange}
				onCallbackDelete={onCallbackDelete}
				ref={refModalAddChangeDeleteCriteria} 
			/>

			<ModalAddChangeDeleteQuestion 
				ref={refModalAddChangeDeleteQuestion} 
			/>

		</>
	);
};


export default BusinessDiagnosticsModel;
